import {post} from '@/utils/request';
import {getBusinessOrganizationKey, getLegacyOrganizationKey} from '@/utils/business-route';
import i18n from '@/locales/init';

/**
 * 组织入口上下文（organization-scope-plan §3.1）。
 *
 * 定制页（org_key）是固定组织入口，携带服务端签发的短期 login_context；
 * 通用页是公共入口，使用 organization_auth_version 协议跨组织查找账号。
 *
 * login_context 是公开入口的作用域信息，不是账号认证或教师授权。
 */

export interface ILoginContextInfo {
  login_context: string;
  /** Older servers omit this field; their context lifetime is ten minutes. */
  expires_in?: number;
  organization_id: string;
  /**
   * The top-level organization which owns this account namespace. This is
   * optional while fixed-entry clients and servers roll out independently.
   */
  account_organization_id?: string;
  organization_code: string;
  organization_name: string;
  is_default: boolean;
}

interface ContextCache {
  key: string;
  pending: Promise<ILoginContextInfo | null> | null;
  resolved: ILoginContextInfo | null;
  expiresAt: number;
}

let cache: ContextCache = {key: '', pending: null, resolved: null, expiresAt: 0};
let publicLoginEntry = false;

/** 标记当前 bundle 为公共登录入口。固定组织入口不应调用此函数。 */
export function enablePublicLoginEntry(): void {
  publicLoginEntry = true;
}

export function isPublicLoginEntry(): boolean {
  return publicLoginEntry;
}

/**
 * Identifies a fixed organization entry from the current login URL. Public
 * login pages return null so their session behavior remains unchanged.
 */
export function getFixedOrganizationKey(
  pathname = window.location.pathname,
  search = window.location.search,
): string | null {
  const businessOrgKey = getBusinessOrganizationKey(pathname);
  if (businessOrgKey) {
    return businessOrgKey;
  }

  if (pathname.replace(/\/+$/, '') !== '/org') {
    return null;
  }

  const orgKey = new URLSearchParams(search).get('org')?.trim();
  return orgKey ? getLegacyOrganizationKey(orgKey) : null;
}

function fetchLoginContext(entry: ContextCache): Promise<ILoginContextInfo | null> {
  const requestedAt = Date.now();
  return post<ILoginContextInfo>('/api/student/get-login-context', {
    org_key: entry.key || undefined,
  })
    .then(res => {
      if (cache !== entry || !res.data?.login_context) {
        return null;
      }
      const ttl = typeof res.data.expires_in === 'number' && res.data.expires_in > 0
        && Number.isFinite(res.data.expires_in) ? res.data.expires_in * 1000 : 600_000;
      // Renew early and count network time against the server-issued lifetime.
      entry.expiresAt = requestedAt + ttl - Math.min(30_000, ttl / 10);
      entry.resolved = res.data;
      return res.data;
    })
    .catch(err => {
      console.error('[LoginContext] resolve failed', err);
      return null;
    })
    .finally(() => {
      entry.pending = null;
    });
}

/**
 * 解析（并按 org_key 缓存）当前页面组织的登录上下文。
 * 同一 org_key 复用进行中的请求，过期前自动更新。认证请求必须等待已
 * 解析上下文，失败时绝不能降级为没有作用域的旧登录协议。
 */
export function resolveLoginContext(orgKey: string): Promise<ILoginContextInfo | null> {
  if (cache.key !== orgKey) {
    cache = {key: orgKey, pending: null, resolved: null, expiresAt: 0};
  }
  if (cache.pending) {
    return cache.pending;
  }
  if (cache.resolved && Date.now() < cache.expiresAt) {
    return Promise.resolve(cache.resolved);
  }
  cache.pending = fetchLoginContext(cache);
  return cache.pending;
}

export function isLoginContextInvalid(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'code' in error
    && error.code === 3103;
}

function contextUnavailable(): Error {
  return new Error(i18n.t('common.dauyan_account_login_context_unavailable'));
}

/** Submit with a fresh context; only an explicit scope rejection permits one retry. */
export async function withLoginContext<T>(request: (token: string) => Promise<T>): Promise<T> {
  const fixedKey = getFixedOrganizationKey();
  const orgKey = fixedKey ?? cache.key;
  const pending = resolveLoginContext(orgKey);
  const entry = cache;
  let context = await pending;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (cache !== entry || getFixedOrganizationKey() !== fixedKey || !context) {
      throw contextUnavailable();
    }
    try {
      return await request(context.login_context);
    } catch (error) {
      if (!isLoginContextInvalid(error)) {
        throw error;
      }
      if (cache !== entry || getFixedOrganizationKey() !== fixedKey) {
        throw contextUnavailable();
      }
      // A concurrent request may already have renewed this rejected token.
      if (entry.resolved?.login_context === context.login_context) {
        entry.expiresAt = 0;
      }
      if (attempt === 1) {
        throw contextUnavailable();
      }
      context = await resolveLoginContext(orgKey);
    }
  }
  throw contextUnavailable();
}

/** Last resolved token; authentication requests must use withLoginContext. */
export function getLoginContextToken(): string | undefined {
  return cache.resolved?.login_context;
}

/** Keep metadata after expiry so session confirmation cannot trigger a logout. */
export function getResolvedLoginContext(): ILoginContextInfo | null {
  return cache.resolved;
}

export function requireLoginContextToken(): string {
  const token = getLoginContextToken();
  if (!token) {
    throw new Error(i18n.t('common.dauyan_account_login_context_unavailable'));
  }
  return token;
}
